/* import __COLOCATED_TEMPLATE__ from './data-fields.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';
import type Admin from 'embercom/models/admin';
import { post } from 'embercom/lib/ajax';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  hideBorder?: boolean;
  hideLeftPane?: boolean;
}

type App = { id: string; currentAdmin: Admin; reload: () => void };
type NotificationsService = {
  notifyResponseError: (
    e: Error,
    messages: { default: string },
    options: { responseProvidedErrors: number[] },
  ) => void;
};

export default class StandaloneSalesforceDataFields extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: NotificationsService;
  @service declare store: Store;
  @service declare intl: IntlService;

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get app(): App {
    return this.finStandaloneService.app;
  }

  get salesforceConfig() {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get synchronizedUserFields(): ExternalFieldIdentifier[] {
    return this.salesforcePlatform.userFields.filter(
      (field) => field.synchronized || field.isSynchronizing,
    );
  }

  get unsynchronizedUserFields(): ExternalFieldIdentifier[] {
    return this.salesforcePlatform.userFields.filter(
      (field) => !field.synchronized && !field.isSynchronizing,
    );
  }

  get synchronizedCaseFields(): ExternalFieldIdentifier[] {
    return this.salesforcePlatform.ticketFields.filter(
      (field) => field.synchronized || field.isSynchronizing,
    );
  }

  get unsynchronizedCaseFields(): ExternalFieldIdentifier[] {
    return this.salesforcePlatform.ticketFields.filter(
      (field) => !field.synchronized && !field.isSynchronizing,
    );
  }

  @action async addSalesforceUserField(userField: ExternalFieldIdentifier) {
    try {
      userField.isSynchronizing = true;

      await userField.synchronize();
      this.app.reload();
      await this.salesforcePlatform.loadDataFields();
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.notifications.fin-service-errors.field-sync-error'),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
    } finally {
      userField.isSynchronizing = false;
    }
  }

  @action async addSalesforceCaseField(caseField: ExternalFieldIdentifier) {
    try {
      caseField.isSynchronizing = true;
      await post(
        `/ember/standalone/salesforce_data/${caseField.externalId}/synchronize_case_field`,
        {
          app_id: this.app.id,
          admin_id: this.app.currentAdmin.id,
        },
      );
      caseField.synchronized = true;
      this.app.reload();
      await this.salesforcePlatform.loadDataFields();
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.notifications.fin-service-errors.field-sync-error'),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
    } finally {
      caseField.isSynchronizing = false;
    }
  }

  @tracked selectedField?: ExternalFieldIdentifier;

  @action didCloseModal() {
    this.selectedField = undefined;
  }

  @action selectField(field: ExternalFieldIdentifier) {
    this.selectedField = field;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::DataFields': typeof StandaloneSalesforceDataFields;
    'standalone/Salesforce/DataFields': typeof StandaloneSalesforceDataFields;
  }
}
